import { Slider, Switch, Tooltip } from 'antd';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import tinycolor from 'tinycolor2';

import { ArrowLeftSvg } from '../../../../assets/icons';
import theme from '../../../../assets/theme';
import { useMetrics } from '../../../../hooks/useMetrics';
import { ActiveBackgroundContext } from '../../../../providers/ActiveBackgroundProvider/ActiveBackgroundProvider';
import { RoundMedia } from '../../../../services/backendService/types';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { Slide } from '../../../SlideComponents/slide.types';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import ColorPickerRow from './ColorPickerRow';
import UploadFilePicker from './UploadFilePicker';

const BackgroundRootTab = ({
  onRoundChange,
  round,
  setBackgroundForAllRounds,
}: {
  onRoundChange: (round: Slide) => void;
  round?: Slide;
  setBackgroundForAllRounds: (media: RoundMedia) => void;
}) => {
  const { t } = useTranslation('Host');

  if (round) {
    return (
      <BackgroundTab
        onRoundChange={onRoundChange}
        setBackgroundForAllRounds={setBackgroundForAllRounds}
        round={round}
      />
    );
  } else {
    return (
      <TabContainer style={{ paddingTop: 20 }}>
        <Title style={{ marginBottom: 10 }} $fontSize={20}>
          {t('Background')}
        </Title>
        <h4>{t('Select a slide or another tab to get started')}</h4>
      </TabContainer>
    );
  }
};

const BackgroundTab = ({
  onRoundChange,
  round,
  setBackgroundForAllRounds,
}: {
  onRoundChange: (round: Slide) => void;
  round: Slide;
  setBackgroundForAllRounds: (media: RoundMedia) => void;
}) => {
  const { t } = useTranslation('Host');
  const metrics = useMetrics();
  const [paneScreen, setPaneScreen] = useState<'default' | 'upload'>('default');

  const { backgroundMedia, setBackgroundMedia, setTextColor } = useContext(
    ActiveBackgroundContext,
  );

  const handleApplyToAll = () => {
    metrics.logEvent('Creator.BackgroundMedia.ApplyToAll.Clicked');
    if (backgroundMedia && round?.backgroundMedia?.fileId === backgroundMedia.fileId) {
      setBackgroundMedia(undefined);
      setTextColor(undefined);
      return;
    }
    if (round?.backgroundMedia) {
      setBackgroundForAllRounds(round.backgroundMedia);
      setBackgroundMedia(round.backgroundMedia);
    }
    //TODO: apply to all should also set the locked element colors
    // const slateRoundTitle = parseStringToSlateObject(round?.title);
    // if (slateRoundTitle) {
    //   setTextColor(slateRoundTitle?.[0].color);
    // }
  };

  const handleApplyBrightness = useCallback(
    (newBrightness: number) => {
      if (round?.backgroundMedia) {
        metrics.logEvent('Creator.BackgroundMedia.Brightness.Changed', {
          newBrightness,
        });
        onRoundChange({
          ...round,
          backgroundMedia: { ...round.backgroundMedia, brightness: 1 - newBrightness },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [round, onRoundChange],
  );

  const handleSrcImageSelected = ({
    src,
    mimeType,
    downloadLocation,
    brightness,
    thumbnailSrc,
  }: {
    imageId: string;
    src: string;
    downloadLocation?: string;
    thumbnailSrc?: string;
    brightness?: number;
    mimeType: 'image/color';
  }) => {
    onRoundChange({
      ...round,
      backgroundMedia: {
        fileId: src,
        fileMimeType: mimeType,
        thumbnailSrc: thumbnailSrc || null,
        brightness: brightness ? brightness : null,
        downloadLocation: downloadLocation,
      },
    });
  };

  const handleColorSelected = (selectedColor: string) => {
    metrics.logEvent('Creator.BackgroundMedia.Submit', {
      type: 'color',
    });
    // due to some dark colors not aligning with Pin code font color we want to check the brightness
    const color = tinycolor(selectedColor);
    const brightness = color.getBrightness();
    handleSrcImageSelected({
      src: `${selectedColor}`,
      imageId: `color:${selectedColor}`,
      mimeType: 'image/color',
      brightness: brightness < 100 ? 0.49 : 0.5,
    });
  };

  const handleResetBackground = () => {
    onRoundChange({
      ...round,
      backgroundMedia: undefined,
    });
  };
  return (
    <TabContainer style={{ paddingTop: 20, paddingRight: 0, paddingLeft: 0 }}>
      {paneScreen === 'default' && (
        <>
          <div style={{ padding: '0px 20px' }}>
            <Title $fontSize={20}>{t('Background')}</Title>
          </div>
          <BackgroundTabContainer>
            <Button
              styles={{ background: theme.colors.blue }}
              onClick={() => {
                setPaneScreen('upload');
              }}
            >
              {t('Upload files')}
            </Button>
            {round && (
              <>
                <ColorPickerRow onColorSelected={handleColorSelected} />

                {round.backgroundMedia && (
                  <div style={{ padding: 0 }}>
                    <span style={{ padding: '1%' }}>{t('Brightness')}</span>
                    <Slider
                      step={0.01}
                      trackStyle={{
                        backgroundColor: theme.colors.blue,
                      }}
                      handleStyle={{
                        backgroundColor: theme.colors.blue,
                        borderColor: theme.colors.blue,
                      }}
                      min={0.01}
                      max={1}
                      onChange={v => {
                        handleApplyBrightness(1 - v);
                      }}
                      value={round.backgroundMedia.brightness || 0.5}
                    />
                  </div>
                )}

                {round.backgroundMedia && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip
                      title={t('Apply this background to all slides')}
                      mouseEnterDelay={0.5}
                    >
                      <span style={{ padding: '1%' }}>{t('Apply to all')}</span>
                    </Tooltip>
                    <Tooltip
                      title={t('Apply this background to all slides')}
                      mouseEnterDelay={0.5}
                    >
                      <StyledSwitch
                        $isChecked={
                          backgroundMedia &&
                          round.backgroundMedia?.fileId === backgroundMedia?.fileId &&
                          round.backgroundMedia.brightness === backgroundMedia.brightness
                        }
                        checked={
                          backgroundMedia &&
                          round.backgroundMedia?.fileId === backgroundMedia?.fileId &&
                          round.backgroundMedia.brightness === backgroundMedia.brightness
                        }
                        onClick={handleApplyToAll}
                      />
                    </Tooltip>
                  </div>
                )}
              </>
            )}
            {round.backgroundMedia && (
              <Button type="ghost" onClick={handleResetBackground}>
                {t('Remove background')}
              </Button>
            )}
          </BackgroundTabContainer>
        </>
      )}
      {paneScreen === 'upload' && (
        <>
          <BackButtonRowContainer onClick={() => setPaneScreen('default')}>
            <IconWrapper height="30px" width="30px" color={theme.colors.black}>
              <ArrowLeftSvg />
            </IconWrapper>
            <Title $fontSize={20}>{t('Upload')}</Title>
          </BackButtonRowContainer>
          <ContentContainer style={{ padding: '0px 20px' }}>
            <UploadFilePicker
              round={round}
              selectedElement={null}
              onRoundChange={onRoundChange}
              isBackgroundMedia={true}
            />
          </ContentContainer>
        </>
      )}
    </TabContainer>
  );
};

const ContentContainer = styled.div`
  height: calc(100% - 30px);
`;

const BackButtonRowContainer = styled.div`
  display: flex;
  height: 30px;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
  padding: 0px 20px;
`;

export default BackgroundRootTab;

const BackgroundTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  gap: 20px;
  width: 100%;
  height: calc(100% - 28px);
  overflow-y: auto;
  padding-bottom: 40px;
  padding-top: 10px;
  padding-left: 20px;
  padding-right: 20px;
`;

const StyledSwitch = styled(Switch)<{ $isChecked?: boolean }>`
  background: ${({ $isChecked }) =>
    $isChecked ? theme.colors.orange : theme.colors.grey};
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  margin-bottom: 0px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;
